import type { CustomFieldObject } from '@clockwise/shared';
import { BaseClient } from '../base-client';
import type {
  CreateCustomFieldInput,
  CustomField,
  ListCustomFieldsQuery,
  PatchCustomFieldInput,
} from './types';

type One = { data: CustomField };
type Many = { data: CustomField[] };
type ListResponse = {
  data: CustomField[];
  meta: { total: number; limit: number; offset: number };
};

export class CustomFieldsClient extends BaseClient {
  list(query: ListCustomFieldsQuery = {}): Promise<ListResponse> {
    return this.request('/api/custom-fields', { query: normalize(query) });
  }

  /**
   * All active definitions for the caller's tenant. Web app fetches this
   * once per session and slices by object client-side — replaces three
   * `byObject` round-trips per page with one. Use `byObject` instead
   * from agents that only need one slice.
   */
  byTenant(): Promise<Many> {
    return this.request('/api/custom-fields/by-tenant');
  }

  /** Active fields enabled for an object — kept for agent consumers. */
  byObject(object: CustomFieldObject): Promise<Many> {
    return this.request(`/api/custom-fields/by-object/${encodeURIComponent(object)}`);
  }

  get(id: string): Promise<One> {
    return this.request(`/api/custom-fields/${encodeURIComponent(id)}`);
  }

  create(input: CreateCustomFieldInput): Promise<One> {
    return this.request('/api/custom-fields', { method: 'POST', body: input });
  }

  patch(id: string, input: PatchCustomFieldInput): Promise<One> {
    return this.request(`/api/custom-fields/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  remove(id: string): Promise<One> {
    return this.request(`/api/custom-fields/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
    });
  }

  restore(id: string): Promise<One> {
    return this.request(`/api/custom-fields/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: {},
    });
  }
}

function normalize(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}
